import {
  type FocusEventHandler,
  type HTMLAttributeAnchorTarget,
  type MouseEventHandler,
  type ReactNode,
  type Ref,
} from 'react';
import classNames from 'classnames';
import { Link, type To } from 'react-router-dom';
import styles from './Button.module.scss';

type ConditionalProps =
  | {
      as?: 'button';
      to?: never;
      disabled?: boolean;
      replace?: never;
      state?: never;
      submit?: boolean;
      target?: never;
      value?: string;
    }
  | {
      as?: 'link';
      to: To;
      disabled?: never;
      replace?: boolean;
      state?: object;
      submit?: never;
      target?: HTMLAttributeAnchorTarget;
      value?: never;
    };

type ButtonProps = {
  ariaLabel?: string;
  children: ReactNode;
  className?: string;
  loading?: boolean;
  onClick?: MouseEventHandler;
  onMouseEnter?: MouseEventHandler;
  onFocus?: FocusEventHandler;
  ref?: Ref<HTMLButtonElement>;
  size?: 'regular' | 'slim' | 'small';
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'pill'
    | 'alert'
    | 'alert-muted'
    | 'text';
};

const Button = ({
  ariaLabel,
  as = 'button',
  children,
  className,
  disabled,
  loading,
  onClick,
  ref,
  replace = false,
  size = 'regular',
  state,
  submit,
  target,
  to = '',
  value,
  variant = 'primary',
  ...rest
}: ButtonProps & ConditionalProps) => {
  if (as === 'link') {
    return (
      <Link
        to={to}
        onClick={onClick}
        className={classNames(
          styles.button,
          styles[variant],
          styles[size],
          className,
          { [styles.loading]: loading }
        )}
        aria-label={ariaLabel}
        replace={replace}
        target={target}
        state={state}
        {...rest}
      >
        <span className={styles.contents}>{children}</span>
        <span className={styles.spin} />
      </Link>
    );
  }

  return (
    <button
      ref={ref}
      disabled={loading || disabled}
      onClick={onClick}
      type={submit ? 'submit' : 'button'}
      className={classNames(
        styles.button,
        styles[variant],
        styles[size],
        className,
        { [styles.loading]: loading }
      )}
      aria-label={ariaLabel}
      value={value}
      {...rest}
    >
      <span className={styles.contents}>{children}</span>
      <span className={styles.spin} />
    </button>
  );
};

export default Button;
